import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Sparkles, 
  Clock, 
  Home,
  Droplet,
  Wind,
  Star,
  ArrowRight
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const DeepCleaningPage = () => {
  const faqs = [
    {
      question: "What's the difference between deep cleaning and regular cleaning?",
      answer: "Regular cleaning maintains surfaces and keeps things tidy. Deep cleaning goes further—baseboards, inside appliances, ceiling fans, window tracks, grout lines, and all those areas that get overlooked in weekly maintenance. It's the foundation for a truly clean home."
    },
    {
      question: "How long does a deep clean take?",
      answer: "Most homes take 4-8 hours depending on size and condition. We never rush the process. Heather's 15+ years of executive housekeeping experience means we know exactly what needs attention and how long quality work actually takes."
    },
    {
      question: "How often should I schedule a deep clean?",
      answer: "Most Brevard County homeowners deep clean 2-4 times per year, or when moving in/out. Beachside homes often need it more frequently due to salt air and sand. We'll recommend a schedule based on your home's specific needs."
    },
    {
      question: "Do you bring your own supplies and equipment?",
      answer: "Yes, we arrive fully equipped with professional-grade supplies, vacuums, and tools. If you have specific product preferences or sensitivities, we're happy to use what you provide."
    },
    {
      question: "Can you deep clean just one room or area?",
      answer: "Absolutely. Many clients start with a kitchen or bathroom deep clean before committing to a whole-home service. We're flexible and will work with your priorities."
    }
  ];

  const whatWeClean = [
    {
      area: "Kitchen",
      tasks: [
        "Inside oven, microwave, and refrigerator",
        "Cabinet fronts and interiors (if requested)",
        "Backsplash grout and tile",
        "Appliance exteriors polished",
        "Baseboards and corners"
      ]
    },
    {
      area: "Bathrooms",
      tasks: [
        "Grout scrubbing and tile restoration",
        "Exhaust fan cleaning",
        "Cabinet interiors",
        "Behind toilets and tight spaces",
        "Fixture polishing"
      ]
    },
    {
      area: "Living Spaces",
      tasks: [
        "Ceiling fans and light fixtures",
        "Window sills and tracks",
        "Baseboards throughout",
        "Behind and under furniture",
        "Vent covers removed and cleaned"
      ]
    },
    {
      area: "Bedrooms",
      tasks: [
        "Closet floors and corners",
        "Under beds and furniture",
        "Window treatments dusted",
        "Light switches and outlet covers",
        "Door frames and trim"
      ]
    }
  ];

  const whyChoosePierce = [
    {
      title: "We Know Brevard Homes",
      description: "From the salt air in Satellite Beach to the new construction in Viera, we understand the unique cleaning challenges facing local homeowners. Heather's 15+ years of executive housekeeping experience means we've seen it all."
    },
    {
      title: "Genuinely Local",
      description: "Bob and Heather live and work in Palm Bay. We're not a franchise sending rotating crews. You'll work directly with us and see the same trusted faces every time."
    },
    {
      title: "No Shortcuts",
      description: "Deep cleaning takes time when done right. We don't rush through checklists—we clean until it's actually done. That's the Pierce Premier standard."
    },
    {
      title: "Long-Term Relationships",
      description: "Many of our deep cleaning clients become recurring customers. Once you experience the difference, you'll understand why Brevard County families trust us with their homes year after year."
    }
  ];

  // Structured Data
  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "Deep Cleaning Services | Pierce Premier Cleaning",
      description: "Professional deep cleaning services in Brevard County, FL. Locally owned with 15+ years experience. Fully insured and 5-star rated.",
      url: "/services/deep-cleaning"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Services", url: "/services" },
      { name: "Deep Cleaning" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "Deep Cleaning Service",
      description: "Comprehensive deep cleaning service covering every corner of your home. Professional, thorough, and trusted by Brevard County residents."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="Deep Cleaning Services | Pierce Premier | Brevard County, FL"
        description="Professional deep cleaning in Brevard County. Locally owned by Bob & Heather, 15+ years experience, fully insured. Same trusted team every visit. Free quotes."
        canonical="/services/deep-cleaning"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-cyan-50 via-blue-50 to-purple-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <Badge className="bg-cyan-600 text-white mb-4">Premium Service</Badge>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  Deep Cleaning Services
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  The kind of clean that resets your entire home. We go beyond surfaces to tackle the areas most cleaning services skip—grout lines, baseboards, inside appliances, and every corner that deserves attention.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-cyan-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-cyan-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-cyan-600 text-cyan-600 px-8 py-4 rounded-lg font-semibold hover:bg-cyan-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1628177142898-93e36e4e3a50?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional deep cleaning in Brevard County home"
                  className="rounded-2xl shadow-2xl"
                />
                <div className="absolute -bottom-6 -left-6 bg-white p-6 rounded-xl shadow-xl border-4 border-cyan-100 hidden lg:block">
                  <div className="flex items-center gap-3">
                    <Star className="w-8 h-8 text-yellow-500 fill-yellow-500" />
                    <div>
                      <div className="font-bold text-2xl text-gray-900">5.0</div>
                      <div className="text-sm text-gray-600">Trusted Locally</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* What We Clean Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                What&apos;s Included in a Deep Clean?
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Every home is different, but here&apos;s what we focus on in a typical deep cleaning service. Nothing gets overlooked.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whatWeClean.map((section, idx) => (
                <Card key={idx} className="border-2 hover:border-cyan-200 transition-colors">
                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-12 h-12 bg-gradient-to-br from-cyan-400 to-blue-500 rounded-xl flex items-center justify-center">
                        <Sparkles className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-2xl font-bold text-gray-900">{section.area}</h3>
                    </div>
                    <ul className="space-y-3">
                      {section.tasks.map((task, taskIdx) => (
                        <li key={taskIdx} className="flex items-start gap-3">
                          <CheckCircle2 className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700">{task}</span>
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Why Choose Pierce Premier */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Choose Pierce Premier?
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                We&apos;re not just another cleaning company. We&apos;re your neighbors, and we care about the work we do in your home.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {whyChoosePierce.map((reason, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-8">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{reason.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{reason.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* When to Schedule Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              When Should You Deep Clean?
            </h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                "Moving into or out of a home",
                "Before or after major holidays",
                "Spring or fall seasonal refresh",
                "After renovation or construction",
                "Before listing your home for sale",
                "When regular cleaning isn't enough"
              ].map((scenario, idx) => (
                <div key={idx} className="flex items-start gap-3 p-4 bg-cyan-50 rounded-lg">
                  <Clock className="w-5 h-5 text-cyan-600 flex-shrink-0 mt-1" />
                  <span className="text-gray-900 font-medium">{scenario}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions About Deep Cleaning
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-4">Still have questions?</p>
              <Link to="/faq" className="text-cyan-600 font-semibold hover:text-cyan-700">
                View Full FAQ →
              </Link>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-4xl text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Serving Brevard County Communities
            </h2>
            <p className="text-gray-600 mb-8">
              Based in Palm Bay and proudly serving families throughout Brevard County—from the beachside communities to Viera and beyond.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "Palm Bay", "Melbourne", "Satellite Beach", "Indialantic", 
                "Indian Harbour Beach", "Cocoa Beach", "Merritt Island", 
                "Viera", "Rockledge", "West Melbourne"
              ].map((city, idx) => (
                <Badge key={idx} variant="outline" className="text-sm py-2 px-4">
                  {city}
                </Badge>
              ))}
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready for a Deep Clean?"
          description="Let's restore your home to its best. Free quotes, flexible scheduling, and the Pierce Premier quality guarantee."
          primaryButtonText="Request Free Quote"
        />
      </div>
    </>
  );
};

export default DeepCleaningPage;
